import * as React from 'react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';
import Skeleton from '@mui/material/Skeleton';

import * as util from '../../utils/util';
import { CarePlanExercise, useCarePlan } from '../../utils/carePlan';

// 케어 맞춤운동(CareSports)이 쓰는 것과 같은 아이콘
import yoga from '../../assets/icon/yoga.gif';
import lifting from '../../assets/icon/lifting.gif';
import colorrunning from '../../assets/icon/colorrunning.gif';
import { goLoginAndReturn } from "../../utils/loginRedirect";

/**
 * 케어 > 맞춤운동(CareSports)의 운동 3종 요약.
 *
 * CareSports 와 같은 AP_004_002 의 값을 쓴다:
 *   stretching[].exercise_time 합  → 스트레칭
 *   weight[].exercise_time 합      → 근력운동
 *   exercisetime                   → 유산소(조깅 기준)
 *
 * 타일 모양은 이 컴포넌트를 쓰는 화면(디지털트윈 화면 2)의 기존 3칸 배치를 그대로
 * 따른다 — 카드 껍데기(SectionCard)는 호출부가 씌운다.
 */

/** exercise_time 이 문자열로 오는 경우가 있어 합계 전에 숫자로 바꾼다 */
const sumMinutes = (list?: CarePlanExercise[]) =>
  (list ?? []).reduce((sum, item) => sum + (Number(item?.exercise_time) || 0), 0);

const minutes = (n: number) => `약 ${Math.round(Math.abs(n))}분`;

const CARE_SPORTS_PATH = '/care/CareSports';
/** 맞춤플랜이 없을 때 보내는 설정 화면 (Care.handleStartPlan 과 동일) */
const CARE_START_PATH = '/care/CareStep';

const MainCareSports = () => {
  const navigate = useNavigate();

  // AP_004_002 는 MainCarePlan 과 모듈 캐시를 공유한다 (한 화면에 둘 다 있어도 요청 1회)
  const { data: carePlan, loading } = useCarePlan();

  const isLogin = util.strNull(window.localStorage.userCusID) !== '';
  const existPlan = carePlan?.exists ?? false;

  const times = useMemo(
    () => ({
      stretching: sumMinutes(carePlan?.data?.stretching),
      weight: sumMinutes(carePlan?.data?.weight),
      aerobic: Number(carePlan?.data?.exercisetime) || 0,
    }),
    [carePlan],
  );

  /** 미로그인 → 로그인 / 플랜 없음 → 설정 / 있음 → 맞춤운동 (케어 홈과 같은 순서) */
  const goSports = () => {
    if (!isLogin) { goLoginAndReturn(navigate, '/care'); return; }
    else navigate(existPlan ? CARE_SPORTS_PATH : CARE_START_PATH);
  };

  const items = [
    { label: '스트레칭', icon: yoga, time: minutes(times.stretching) },
    { label: '근력운동', icon: lifting, time: minutes(times.weight) },
    { label: '유산소', icon: colorrunning, time: `조깅 ${Math.round(Math.abs(times.aerobic))}분` },
  ];

  return (
    <React.Fragment>
      <Stack direction="row" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography sx={{ color: '#333', fontSize: 15, fontWeight: 800 }}>맞춤운동</Typography>
        {existPlan && (
          <ButtonBase
            onClick={goSports}
            sx={{ ml: 'auto', color: '#888', fontSize: 13, fontWeight: 600, borderRadius: '8px', px: 0.5 }}
          >
            전체보기 ›
          </ButtonBase>
        )}
      </Stack>

      {loading ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.2 }}>
          {[0, 1, 2].map((slot) => (
            <Box key={slot}>
              <Skeleton variant="rounded" sx={{ width: '100%', aspectRatio: '1 / 0.82', borderRadius: '14px' }} />
              <Skeleton height={18} sx={{ mt: 0.8 }} />
              <Skeleton height={16} width="70%" sx={{ mx: 'auto' }} />
            </Box>
          ))}
        </Box>
      ) : !isLogin || !existPlan ? (
        /* 운동 시간은 맞춤플랜에서 나온다 — 없는 값을 0분으로 보여주지 않는다 */
        <ButtonBase
          onClick={goSports}
          sx={{
            width: '100%',
            display: 'block',
            textAlign: 'center',
            borderRadius: '12px',
            bgcolor: '#f3f6fa',
            p: 2,
          }}
        >
          <Typography sx={{ fontSize: 13, color: '#666', lineHeight: 1.5 }}>
            {isLogin
              ? '맞춤플랜을 시작하면 나에게 맞는 운동량을 알려드립니다.'
              : '로그인하면 나에게 맞는 운동량을 알려드립니다.'}
          </Typography>
          <Typography sx={{ mt: 1, fontSize: 13, fontWeight: 800, color: '#EF8322' }}>
            {isLogin ? '맞춤플랜 시작' : '로그인 하기'}
          </Typography>
        </ButtonBase>
      ) : (
        <React.Fragment>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.2 }}>
            {items.map((item) => (
              <ButtonBase
                key={item.label}
                onClick={goSports}
                sx={{
                  // block 이면 내용이 짧은 칸이 세로 가운데로 밀린다 (<button> 기본 정렬)
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  justifyContent: 'flex-start',
                  width: '100%',
                  borderRadius: '14px',
                }}
              >
                {/* 아이콘만 박스 안 — 정사각형, 흰 배경, 연한 외곽선/그림자 */}
                <Box
                  sx={{
                    aspectRatio: '1 / 0.82',
                    flexShrink: 0,
                    bgcolor: '#fff',
                    border: '1px solid #E6E6E6',
                    borderRadius: '14px',
                    boxShadow: '0 2px 6px rgba(0,0,0,.05)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Box
                    component="img"
                    src={item.icon}
                    alt={item.label}
                    sx={{ width: 50, height: 50, objectFit: 'contain' }}
                  />
                </Box>
                <Typography sx={{ mt: 0.8, color: '#333', fontSize: 13, fontWeight: 700, textAlign: 'center' }}>
                  {item.label}
                </Typography>
                <Typography sx={{ color: '#54a4cb', fontSize: 12, fontWeight: 800, textAlign: 'center' }}>
                  {item.time}
                </Typography>
              </ButtonBase>
            ))}
          </Box>
          <Typography sx={{ mt: 2, color: '#333', fontSize: 15, fontWeight: 800, textAlign: 'center' }}>
            하루에 이 만큼 운동하세요!
          </Typography>
        </React.Fragment>
      )}
    </React.Fragment>
  );
}

export default MainCareSports;
